import type { AdminCompaniesListParams } from '@/api/(admin)/companies/types';
import type { AdminCompaniesQueryInput } from '../-types';

/**
 * Filters the endpoint takes a *list* for. `FILTER_COLUMNS` in
 * `modules/companies/core/db/companies/list-admin.ts` marks these `arrayable`
 * (country, isDemoCompany, isClientCompany) or matched by JSON regex
 * (facilityProcesses), so a comma-joined value means "any of these". Everything else is `useLike`, where
 * `LIKE '%a,b%'` matches nothing — those stay single-valued.
 */
const MULTI_VALUE_FILTERS = new Set([
  'country',
  'facilityProcesses',
  'isDemoCompany',
  'isClientCompany',
]);

/**
 * Turns the table's state into the endpoint's parameters.
 *
 * One conversion point, as in `management/users`: the query key is the object
 * this returns, so the route loader and the component's hook only share a cache
 * entry when both derive their params the same way.
 *
 * Two translations happen:
 *
 *  - **Page base.** The table counts pages from 1; `AdminCompaniesListQuerySchema`
 *    takes `page` from 0, so sending the UI's number straight through would skip
 *    the first page.
 *  - **Filters.** The endpoint takes one JSON object of `column: value`. Values
 *    are joined with commas only for the columns that can actually read a list
 *    (see above); for the rest only the first value is sent, since a joined
 *    `LIKE` would silently match nothing.
 */
export function toAdminCompaniesParams(
  input: AdminCompaniesQueryInput,
): AdminCompaniesListParams {
  const filter: Record<string, string> = {};
  for (const [key, values] of Object.entries(input.filters ?? {})) {
    if (!values || values.length === 0) continue;
    const value = MULTI_VALUE_FILTERS.has(key)
      ? values.join(',')
      : (values[0] as string);
    if (value !== '') filter[key] = value;
  }
  const hasFilter = Object.keys(filter).length > 0;

  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    ...(input.sort ? { sort: input.sort } : {}),
    ...(hasFilter ? { filter: JSON.stringify(filter) } : {}),
  };
}

/** Serialises already-converted params. */
export function buildAdminCompaniesQs(
  params: AdminCompaniesListParams,
): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}
